// ─────────────────────────────────────────────────────────────────────────────
// cms-envelope.util (WB-7860)
// ─────────────────────────────────────────────────────────────────────────────
// Unwraps CAdES / PKCS#7 signature envelopes (`.p7m`) so the previewer renders
// the signed document instead of the DER wrapper.
//
// These files are SIGNED, not encrypted: the original bytes sit in the clear in
// `encapContentInfo.eContent`, so recovering them is pure ASN.1 walking — no
// keys, no certificates, no signature verification.
//
// Real PEC (posta certificata) files mix every BER length form in ONE file, so
// the reader below has to handle all of them. From an actual Euroclone order:
//   - outer envelope: indefinite lengths (`30 80` … `00 00`)
//   - outer eContent: constructed OCTET STRING (`24 80`) in ~1 KB segments
//   - inner envelope: definite long-form lengths (`83 0D 66 B2`)
// A reader that handles only the primitive/definite case silently returns the
// first 1 KB chunk, which is the failure mode this util exists to avoid.
// ─────────────────────────────────────────────────────────────────────────────

/** 1.2.840.113549.1.7.2 — id-signedData, the only envelope we unwrap. */
const OID_SIGNED_DATA = [0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x07, 0x02];

const TAG_OCTET_STRING = 0x04;
const TAG_OID = 0x06;
const TAG_OCTET_STRING_CONSTRUCTED = 0x24;
const TAG_SEQUENCE = 0x30;
const TAG_CONTEXT_0 = 0xa0;

/** Envelopes nest — PEC re-signs an already-signed attachment (`.pdf.p7m.p7m`). 4 is far past real. */
const MAX_UNWRAP_DEPTH = 4;

/** Stack guard for the readTlv ↔ findIndefiniteEnd ↔ readOctets recursion on a malformed file. */
const MAX_TLV_DEPTH = 32;

/** Trailing signature extension, stripped repeatedly: `ORDINE.pdf.p7m.p7m` → `ORDINE.pdf`. */
const SIGNATURE_EXTENSION = /\.(p7m|p7s)$/i;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface Tlv {
  tag: number;
  /** Offset of the first content byte. */
  start: number;
  /** Offset one past the last content byte. */
  end: number;
  /** Offset one past the whole TLV — past the `00 00` terminator when indefinite. */
  next: number;
}

/** One tag-length-value at `pos`, or null if it is malformed or runs past the buffer. */
function readTlv(bytes: Uint8Array, pos: number, depth: number): Tlv | null {
  if (depth > MAX_TLV_DEPTH || pos < 0 || pos + 1 >= bytes.length) {
    return null;
  }
  const tag = bytes[pos];
  // High-tag-number form never appears in CMS; refuse rather than mis-walk the structure.
  if ((tag & 0x1f) === 0x1f) {
    return null;
  }
  const lengthByte = bytes[pos + 1];

  if (lengthByte === 0x80) {
    const start = pos + 2;
    const end = findIndefiniteEnd(bytes, start, depth + 1);
    return end === null ? null : { tag, start, end, next: end + 2 };
  }

  if (lengthByte & 0x80) {
    const count = lengthByte & 0x7f;
    // 4 bytes ≈ 4 GB. Anything wider is malformed, and beyond 2^53 the arithmetic would lose precision.
    if (count === 0 || count > 4 || pos + 2 + count > bytes.length) {
      return null;
    }
    let length = 0;
    for (let i = 0; i < count; i++) {
      length = length * 256 + bytes[pos + 2 + i];
    }
    const start = pos + 2 + count;
    const end = start + length;
    return end > bytes.length ? null : { tag, start, end, next: end };
  }

  const start = pos + 2;
  const end = start + lengthByte;
  return end > bytes.length ? null : { tag, start, end, next: end };
}

/** Offset of the `00 00` closing an indefinite-length TLV whose content starts at `start`. */
function findIndefiniteEnd(bytes: Uint8Array, start: number, depth: number): number | null {
  let pos = start;
  while (pos + 1 < bytes.length) {
    if (bytes[pos] === 0x00 && bytes[pos + 1] === 0x00) {
      return pos;
    }
    const child = readTlv(bytes, pos, depth);
    // `next <= pos` would spin forever on a zero-length malformed element.
    if (!child || child.next <= pos) {
      return null;
    }
    pos = child.next;
  }
  return null;
}

function isOid(bytes: Uint8Array, tlv: Tlv, expected: number[]): boolean {
  if (tlv.tag !== TAG_OID || tlv.end - tlv.start !== expected.length) {
    return false;
  }
  for (let i = 0; i < expected.length; i++) {
    if (bytes[tlv.start + i] !== expected[i]) {
      return false;
    }
  }
  return true;
}

/** Content of an OCTET STRING, concatenating the segments of the constructed (`24`) form. */
function readOctets(bytes: Uint8Array, tlv: Tlv, depth: number): Uint8Array | null {
  if (depth > MAX_TLV_DEPTH) {
    return null;
  }
  if (tlv.tag === TAG_OCTET_STRING) {
    return bytes.subarray(tlv.start, tlv.end);
  }
  if (tlv.tag !== TAG_OCTET_STRING_CONSTRUCTED) {
    return null;
  }

  const chunks: Uint8Array[] = [];
  let total = 0;
  let pos = tlv.start;
  while (pos < tlv.end) {
    const child = readTlv(bytes, pos, depth + 1);
    if (!child || child.next <= pos) {
      return null;
    }
    const chunk = readOctets(bytes, child, depth + 1);
    if (!chunk) {
      return null;
    }
    chunks.push(chunk);
    total += chunk.length;
    pos = child.next;
  }
  if (!chunks.length) {
    return null;
  }
  if (chunks.length === 1) {
    return chunks[0];
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/**
 * `eContent` of a SignedData envelope, or null when `bytes` is not one.
 *
 * Every field is tag-checked on the way down, so a non-envelope (or a truncated one) fails fast
 * rather than returning a plausible-looking slice of the wrong thing.
 */
function extractSignedContent(bytes: Uint8Array): Uint8Array | null {
  const contentInfo = readTlv(bytes, 0, 0);
  if (!contentInfo || contentInfo.tag !== TAG_SEQUENCE) {
    return null;
  }
  const contentType = readTlv(bytes, contentInfo.start, 0);
  if (!contentType || !isOid(bytes, contentType, OID_SIGNED_DATA)) {
    return null;
  }
  const wrapper = readTlv(bytes, contentType.next, 0); // [0] EXPLICIT
  if (!wrapper || wrapper.tag !== TAG_CONTEXT_0) {
    return null;
  }
  const signedData = readTlv(bytes, wrapper.start, 0);
  if (!signedData || signedData.tag !== TAG_SEQUENCE) {
    return null;
  }

  const version = readTlv(bytes, signedData.start, 0); // INTEGER
  if (!version) {
    return null;
  }
  const digestAlgorithms = readTlv(bytes, version.next, 0); // SET
  if (!digestAlgorithms) {
    return null;
  }
  const encapContentInfo = readTlv(bytes, digestAlgorithms.next, 0);
  if (!encapContentInfo || encapContentInfo.tag !== TAG_SEQUENCE) {
    return null;
  }
  const eContentType = readTlv(bytes, encapContentInfo.start, 0);
  if (!eContentType || eContentType.tag !== TAG_OID) {
    return null;
  }

  // eContent is OPTIONAL: a detached signature (`.p7s`) carries none, and there is nothing to render.
  // Both bounds checks matter — `certificates` is also [0] and is the very next field AFTER
  // encapContentInfo, so without them a detached signature walks straight into the certificate set.
  if (eContentType.next >= encapContentInfo.end) {
    return null;
  }
  const eContent = readTlv(bytes, eContentType.next, 0);
  if (!eContent || eContent.tag !== TAG_CONTEXT_0 || eContent.end > encapContentInfo.end) {
    return null;
  }
  const octets = readTlv(bytes, eContent.start, 0);
  return octets ? readOctets(bytes, octets, 0) : null;
}

function startsWith(bytes: Uint8Array, magic: number[]): boolean {
  if (bytes.length < magic.length) {
    return false;
  }
  for (let i = 0; i < magic.length; i++) {
    if (bytes[i] !== magic[i]) {
      return false;
    }
  }
  return true;
}

/** Leading `<?xml` / `<`, skipping a UTF-8 BOM and whitespace. */
function looksLikeXml(bytes: Uint8Array): boolean {
  let i = startsWith(bytes, [0xef, 0xbb, 0xbf]) ? 3 : 0;
  while (i < bytes.length && (bytes[i] === 0x20 || bytes[i] === 0x09 || bytes[i] === 0x0a || bytes[i] === 0x0d)) {
    i++;
  }
  return bytes[i] === 0x3c; // '<'
}

/**
 * Conservative text check: no control bytes outside tab/CR/LF/FF in the first 512.
 * Deliberately strict — a false positive here would render binary into a <pre>.
 */
function looksLikeText(bytes: Uint8Array): boolean {
  const limit = Math.min(bytes.length, 512);
  if (!limit) {
    return false;
  }
  for (let i = 0; i < limit; i++) {
    const b = bytes[i];
    if (b === 0x09 || b === 0x0a || b === 0x0c || b === 0x0d) {
      continue;
    }
    if (b < 0x20 || b === 0x7f) {
      return false;
    }
  }
  return true;
}

/**
 * MIME type from magic bytes, or `''` when unrecognized.
 *
 * Only formats the previewer can actually render are listed, and only ones with a strong magic —
 * this doubles as the guard deciding whether an unwrapped payload is safe to use at all.
 */
export function sniffMimeType(bytes: Uint8Array): string {
  if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) {
    return 'application/pdf'; // %PDF
  }
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) {
    return XLSX_MIME; // PK.. — xlsx/ods and the rest of the OOXML family
  }
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0])) {
    return 'application/vnd.ms-excel'; // OLE2 compound file — legacy .xls
  }
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47])) {
    return 'image/png';
  }
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return 'image/jpeg';
  }
  if (startsWith(bytes, [0x47, 0x49, 0x46, 0x38])) {
    return 'image/gif';
  }
  if (looksLikeXml(bytes)) {
    return 'application/xml';
  }
  if (looksLikeText(bytes)) {
    return 'text/plain';
  }
  return '';
}

function isBase64Byte(b: number): boolean {
  return (
    (b >= 0x41 && b <= 0x5a) || // A-Z
    (b >= 0x61 && b <= 0x7a) || // a-z
    (b >= 0x30 && b <= 0x39) || // 0-9
    b === 0x2b || // +
    b === 0x2f || // /
    b === 0x3d || // =
    b === 0x0a ||
    b === 0x0d
  );
}

/**
 * Some `.p7m` arrive base64-armored instead of raw DER. Returns the decoded bytes only when they
 * start a DER SEQUENCE, so a plain text attachment can never be mistaken for an envelope.
 */
function decodeBase64Armor(bytes: Uint8Array): Uint8Array | null {
  if (bytes.length < 4 || bytes[0] === 0x30) {
    return null; // already raw DER (or far too short to be an envelope)
  }
  const head = Math.min(bytes.length, 256);
  for (let i = 0; i < head; i++) {
    if (!isBase64Byte(bytes[i])) {
      return null;
    }
  }
  try {
    let ascii = '';
    // Chunked so a multi-MB attachment can't blow the argument limit of String.fromCharCode.
    for (let i = 0; i < bytes.length; i += 8192) {
      ascii += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 8192)));
    }
    const binary = atob(ascii.replace(/\s/g, ''));
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      out[i] = binary.charCodeAt(i);
    }
    return out.length && out[0] === TAG_SEQUENCE ? out : null;
  } catch {
    return null;
  }
}

/**
 * The signed document inside `input`, unwrapping nested envelopes.
 *
 * Returns the **same reference** when there is nothing to unwrap, so callers can branch on
 * `result !== input` and keep their existing path byte-for-byte untouched. It also returns the
 * original when the recovered payload is a format we don't recognise: a half-parsed slice must
 * never reach the renderer, and leaving it alone is exactly the pre-existing behaviour.
 */
export function unwrapSignatureEnvelopes(input: Uint8Array): Uint8Array {
  let current = decodeBase64Armor(input) ?? input;
  let unwrapped = false;

  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
    const payload = extractSignedContent(current);
    if (!payload || !payload.length) {
      break;
    }
    current = payload;
    unwrapped = true;
  }

  return unwrapped && sniffMimeType(current) ? current : input;
}

/** `ORDINE_270.pdf.p7m.p7m` → `ORDINE_270.pdf`. Unchanged when there is no signature extension. */
export function stripSignatureExtensions(name: string): string {
  let out = name || '';
  while (SIGNATURE_EXTENSION.test(out)) {
    out = out.replace(SIGNATURE_EXTENSION, '');
  }
  return out || name;
}

/** Whether a file name or URL path ends in a signature extension (query/fragment ignored). */
export function hasSignatureExtension(nameOrPath: string): boolean {
  const path = (nameOrPath || '').split('?')[0].split('#')[0];
  return SIGNATURE_EXTENSION.test(path);
}
